/**
 * Kavee custom JavaScript
 * Import theme components and extend them here.
 *
 * Example - extend the Drawer component:
 *   import { Drawer } from "theme";
 *   class CustomDrawer extends Drawer { ... }
 *   customElements.define('custom-drawer', CustomDrawer);
 */

import { SearchDrawer } from 'theme';

/**
 * Mobile keyboards only appear when focus() runs in the same turn as the tap.
 * Impact waits for the open animation (checkCanFocusTrap) and also keyframes
 * visibility hidden→visible, which dismisses the keyboard. Focus now, and lock
 * the host visible until hide() so Motion cannot undo it.
 */
const originalSearchDrawerShow = SearchDrawer.prototype.show;
const originalSearchDrawerHide = SearchDrawer.prototype.hide;

SearchDrawer.prototype.show = function (animate = true) {
  if (this.open) {
    return originalSearchDrawerShow.call(this, animate);
  }

  this.removeAttribute('inert');
  this.style.setProperty('visibility', 'visible', 'important');
  this.querySelector('input:not([type="hidden"])')?.focus({ preventScroll: true });

  return originalSearchDrawerShow.call(this, animate);
};

SearchDrawer.prototype.hide = function () {
  this.style.removeProperty('visibility');
  return originalSearchDrawerHide.call(this);
};

/**
 * Disable hover scale on shopify-account avatar.
 * .account-button__avatar is inside shadow DOM (no part exposed), so we inject override styles.
 */
function injectAccountAvatarOverride(element) {
  if (!element.shadowRoot || element.dataset.kaveeAvatarOverride) return;
  const style = document.createElement('style');
  style.textContent = '.account-button__avatar:hover{scale:1!important;box-shadow:inset 0 0 0 1px #00000014!important}';
  element.shadowRoot.appendChild(style);
  element.dataset.kaveeAvatarOverride = '1';
}

function observeAccountElements() {
  document.querySelectorAll('shopify-account').forEach(injectAccountAvatarOverride);
}

async function initAccountAvatarOverride() {
  await customElements.whenDefined('shopify-account');
  observeAccountElements();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccountAvatarOverride);
} else {
  initAccountAvatarOverride();
}

function startObserver() {
  if (!document.body) return;
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1 && (node.matches?.('shopify-account') || node.querySelector?.('shopify-account'))) {
          observeAccountElements();
          break;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.body) startObserver();
else document.addEventListener('DOMContentLoaded', startObserver);

/**
 * Map a Shopify Ajax Cart line item (cart/add.js) to Made With Intent AddToCart `data` fields.
 * @see https://help.madewithintent.ai/en/articles/9454635-the-intent-tag-sdk
 */
function kaveeNormalizeProductImageUrl(url) {
  if (!url || typeof url !== 'string') return '';
  if (url.indexOf('http') === 0) return url;
  if (url.indexOf('//') === 0) return `https:${url}`;
  return url;
}

function kaveeFormatMoneyFromCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
    const format = window.themeVariables?.settings?.moneyFormat;
    if (format) return window.Shopify.formatMoney(n, format);
  }
  return (n / 100).toFixed(2);
}

function mapLineItemToMwiDetail(item) {
  if (!item || typeof item !== 'object') return {};
  const unitCents =
    item.final_price != null ? item.final_price : item.price != null ? item.price : null;
  const imageSrc =
    item.featured_image && typeof item.featured_image === 'object' && item.featured_image.url
      ? item.featured_image.url
      : typeof item.image === 'string'
        ? item.image
        : '';
  return {
    product_name: item.product_title || item.title || '',
    product_sku: item.sku || '',
    product_price: kaveeFormatMoneyFromCents(unitCents),
    product_currency:
      (typeof window.Shopify !== 'undefined' && window.Shopify.currency && window.Shopify.currency.active) ||
      '',
    product_brand: item.vendor || '',
    product_category: item.product_type || '',
    product_image_url: kaveeNormalizeProductImageUrl(imageSrc),
    product_availability: 1
  };
}

function dispatchAddToCartFromLineItems(items) {
  if (!window.intent || !window.intent.dataLayer || !items || !items.length) return;
  for (let i = 0; i < items.length; i++) {
    const detail = mapLineItemToMwiDetail(items[i]);
    document.dispatchEvent(new CustomEvent('mwi:add-to-cart', { bubbles: true, detail }));
  }
}

window.kaveeMwi = window.kaveeMwi || {};
window.kaveeMwi.dispatchAddToCartFromLineItems = dispatchAddToCartFromLineItems;

document.addEventListener('variant:add', (e) => {
  const items = e.detail?.items;
  if (!items?.length) return;
  dispatchAddToCartFromLineItems(items);
});

// Alpine countdown component used by countdown sections (reads end_date and expired_message from data attributes)
document.addEventListener('alpine:init', () => {
  Alpine.data('countdownTimer', () => ({
    initialized: false,
    expired: false,
    expiredMessage: 'This offer has ended.',
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
    _end: null,
    _timerId: null,

    start() {
      const endDateString = this.$el.dataset.endDate;
      const msg = this.$el.dataset.expiredMessage;
      if (msg) this.expiredMessage = msg;

      if (!endDateString) {
        return;
      }

      const parsed = new Date(endDateString);

      if (isNaN(parsed.getTime())) {
        // Invalid date string; do not start timer
        return;
      }

      this._end = parsed;
      this.initialized = true;
      this.tick();

      this._timerId = setInterval(() => {
        this.tick();
      }, 1000);
    },

    tick() {
      if (!this._end) return;

      const now = new Date();
      let diff = this._end.getTime() - now.getTime();

      if (diff <= 0) {
        diff = 0;
        this.expired = true;
        this.days = '00';
        this.hours = '00';
        this.minutes = '00';
        this.seconds = '00';

        if (this._timerId) {
          clearInterval(this._timerId);
          this._timerId = null;
        }

        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const d = Math.floor(totalSeconds / 86400);
      const h = Math.floor((totalSeconds % 86400) / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;

      this.days = String(d).padStart(2, '0');
      this.hours = String(h).padStart(2, '0');
      this.minutes = String(m).padStart(2, '0');
      this.seconds = String(s).padStart(2, '0');
    }
  }));
});

/**
 * Keep desktop mega menus at a uniform panel height (tallest panel wins)
 * to avoid layout jump when switching top-level menu items.
 */
function applyDesktopMegaMenuUniformHeight() {
  if (!window.matchMedia('(min-width: 1150px)').matches) {
    document.documentElement.style.removeProperty('--kavee-mega-menu-uniform-height');
    return;
  }

  const megaMenus = Array.from(document.querySelectorAll('.header__link-list details[is="mega-menu-disclosure"] > .mega-menu'));
  if (!megaMenus.length) return;

  let maxHeight = 0;

  megaMenus.forEach((menu) => {
    const clone = menu.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.left = '0';
    clone.style.top = '-9999px';
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '1';
    clone.style.mixBlendMode = 'normal';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';

    document.body.appendChild(clone);
    maxHeight = Math.max(maxHeight, clone.offsetHeight);
    clone.remove();
  });

  if (maxHeight > 0) {
    document.documentElement.style.setProperty('--kavee-mega-menu-uniform-height', `${maxHeight}px`);
  }
}

let megaMenuHeightRaf = null;
function scheduleDesktopMegaMenuUniformHeight() {
  if (megaMenuHeightRaf) cancelAnimationFrame(megaMenuHeightRaf);
  megaMenuHeightRaf = requestAnimationFrame(() => {
    megaMenuHeightRaf = null;
    applyDesktopMegaMenuUniformHeight();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleDesktopMegaMenuUniformHeight);
} else {
  scheduleDesktopMegaMenuUniformHeight();
}

window.addEventListener('load', scheduleDesktopMegaMenuUniformHeight);
window.addEventListener('resize', scheduleDesktopMegaMenuUniformHeight);
document.addEventListener('shopify:section:load', scheduleDesktopMegaMenuUniformHeight);

/**
 * Setup toggle picker: maps Stand/Lid switches to hidden Setup option radios
 * so the theme VariantPicker handles variant changes as usual.
 */
const SETUP_TOGGLE_ANIM_MS = 320;
const setupToggleSyncTimers = new WeakMap();

function getSetupCombinationKey(standOn, lidOn) {
  if (standOn && lidOn) return 'both';
  if (standOn) return 'stand';
  if (lidOn) return 'lid';
  return 'base';
}

function syncSetupToggleToVariant(picker) {
  const standToggle = picker.querySelector('[data-setup-toggle="stand"]');
  const lidToggle = picker.querySelector('[data-setup-toggle="lid"]');

  let mapping;
  let availability;
  try {
    mapping = JSON.parse(picker.dataset.setupMapping || '{}');
    availability = JSON.parse(picker.dataset.setupAvailability || '{}');
  } catch (e) {
    return;
  }

  const standOn = Boolean(standToggle?.checked);
  const lidOn = Boolean(lidToggle?.checked);
  const key = getSetupCombinationKey(standOn, lidOn);
  const targetValueId = mapping[key];
  if (!targetValueId) return;

  const fieldset = picker.closest('.variant-picker__option');
  const radio = fieldset?.querySelector(`[data-setup-option-radios] input[value="${targetValueId}"]`);
  if (!radio || radio.checked) return;

  radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
}

document.addEventListener('change', (event) => {
  const toggle = event.target;
  if (!toggle.matches('[data-setup-toggle]')) return;

  const picker = toggle.closest('[data-setup-toggle-picker]');
  if (!picker) return;

  const standToggle = picker.querySelector('[data-setup-toggle="stand"]');
  const lidToggle = picker.querySelector('[data-setup-toggle="lid"]');

  let mapping;
  let availability;
  try {
    mapping = JSON.parse(picker.dataset.setupMapping || '{}');
    availability = JSON.parse(picker.dataset.setupAvailability || '{}');
  } catch (e) {
    return;
  }

  const standOn = Boolean(standToggle?.checked);
  const lidOn = Boolean(lidToggle?.checked);
  const key = getSetupCombinationKey(standOn, lidOn);

  if (availability[key] === false) {
    toggle.checked = !toggle.checked;
    return;
  }

  const existingTimer = setupToggleSyncTimers.get(picker);
  if (existingTimer) clearTimeout(existingTimer);

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const delay = prefersReducedMotion ? 0 : SETUP_TOGGLE_ANIM_MS;

  if (delay === 0) {
    syncSetupToggleToVariant(picker);
    return;
  }

  const timerId = window.setTimeout(() => {
    setupToggleSyncTimers.delete(picker);
    syncSetupToggleToVariant(picker);
  }, delay);

  setupToggleSyncTimers.set(picker, timerId);
});

/**
 * Each x-drawer registers its own Delegate click handler on document.body. When duplicate
 * x-drawer nodes share an id, one click opens multiple stacked drawers. Keep a single
 * canonical instance and handle toggling once in capture phase.
 */
function getCanonicalDrawer(drawerId) {
  const drawers = [...document.querySelectorAll(`x-drawer#${CSS.escape(drawerId)}`)];
  if (!drawers.length) return null;
  if (drawers.length === 1) return drawers[0];

  const closedInRerender = drawers.find(
    (drawer) => drawer.closest('product-rerender') && !drawer.hasAttribute('open')
  );
  const openDrawer = drawers.find((drawer) => drawer.hasAttribute('open'));
  const canonical = closedInRerender || openDrawer || drawers[0];

  drawers.forEach((drawer) => {
    if (drawer !== canonical) drawer.remove();
  });

  return canonical;
}

function dedupeAllDrawers() {
  const drawerIds = new Set(
    [...document.querySelectorAll('x-drawer[id]')].map((drawer) => drawer.id)
  );
  drawerIds.forEach((drawerId) => {
    getCanonicalDrawer(drawerId);
  });
}

document.addEventListener(
  'click',
  (event) => {
    const control = event.target.closest('[aria-controls]');
    if (!control) return;

    const drawerId = control.getAttribute('aria-controls');
    if (!drawerId) return;

    const drawer = getCanonicalDrawer(drawerId);
    if (!drawer) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (drawer.open) {
      drawer.hide();
    } else {
      drawer.show();
    }
  },
  true
);

/**
 * Open content drawers from hash links (e.g. href="#forever-promise").
 */
function openDrawerFromHash(hash) {
  const drawerId = (hash || '').replace(/^#/, '');
  if (!drawerId) return false;

  const drawer = getCanonicalDrawer(drawerId);
  if (!drawer) return false;

  drawer.show();
  return true;
}

document.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    // Prefer aria-controls when both are present (handled above).
    if (link.hasAttribute('aria-controls')) return;

    const drawerId = href.slice(1);
    const drawer = getCanonicalDrawer(drawerId);
    if (!drawer) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    drawer.show();
  },
  true
);

function openDrawerFromLocationHash() {
  if (!window.location.hash) return;
  openDrawerFromHash(window.location.hash);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', openDrawerFromLocationHash);
} else {
  openDrawerFromLocationHash();
}

window.addEventListener('hashchange', openDrawerFromLocationHash);

document.addEventListener(
  'product:rerender',
  () => {
    requestAnimationFrame(dedupeAllDrawers);
  },
  true
);

/**
 * Gift with purchase: when checked, take over the product form submit and add
 * main product + gift in a single /cart/add.js request (mirrors ProductForm
 * success/error handling). Unchecked = leave the theme ProductForm alone.
 *
 * Parallel second requests race with ProductForm and are unreliable on Shopify.
 */
const gwpSubmittingForms = new WeakSet();

function getCheckedGiftOffersForForm(formId) {
  if (!formId) return [];
  return Array.from(
    document.querySelectorAll(`[data-gift-offer][data-gift-form="${CSS.escape(formId)}"]`)
  ).filter((offer) => offer.querySelector('[data-gift-checkbox]')?.checked);
}

function getGiftVariantIds(offers) {
  const ids = [];
  offers.forEach((offer) => {
    const variantId = parseInt(offer.dataset.giftVariantId, 10);
    if (Number.isFinite(variantId) && variantId > 0 && !ids.includes(variantId)) {
      ids.push(variantId);
    }
  });
  return ids;
}

async function submitProductFormWithGifts(form, giftVariantIds) {
  if (gwpSubmittingForms.has(form)) return;
  gwpSubmittingForms.add(form);

  const submitButtons = Array.from(form.elements).filter((button) => button.type === 'submit');
  submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));

  try {
    const root = window.Shopify?.routes?.root || '/';
    const mainVariantInput = form.elements.namedItem('id');
    const mainVariantId = parseInt(
      (mainVariantInput && 'value' in mainVariantInput ? mainVariantInput.value : null) ||
        form.querySelector('[name="id"]')?.value,
      10
    );
    const quantityInput = form.querySelector('[name="quantity"]');
    const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);

    if (!Number.isFinite(mainVariantId) || mainVariantId <= 0) {
      throw new Error('Missing main product variant id');
    }

    const items = [{ id: mainVariantId, quantity }];
    giftVariantIds.forEach((id) => {
      if (id !== mainVariantId) items.push({ id, quantity: 1 });
    });

    // Preserve line item properties from the product form (gift card recipient, custom props, etc.)
    const formData = new FormData(form);
    const properties = {};
    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    });
    if (Object.keys(properties).length) {
      items[0].properties = properties;
    }

    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items,
        sections: sectionsToBundle.join(','),
        sections_url: `${root}variants/${mainVariantId}`
      })
    });

    const responseJson = await response.json();

    if (response.ok) {
      const addedItems = responseJson.items ? responseJson.items : [responseJson];

      if (
        window.themeVariables?.settings?.cartType === 'page' ||
        window.themeVariables?.settings?.pageType === 'cart'
      ) {
        if (typeof window.kaveeMwi?.dispatchAddToCartFromLineItems === 'function') {
          window.kaveeMwi.dispatchAddToCartFromLineItems(addedItems);
        }
        window.location.href = `${root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      form.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: addedItems, cart: cartContent }
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent }
        })
      );
    } else {
      form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: responseJson.description }
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  } catch (err) {
    console.error('Gift with purchase cart error:', err);
    form.dispatchEvent(
      new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: 'Unable to add items to cart. Please try again.' }
      })
    );
  } finally {
    submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
    gwpSubmittingForms.delete(form);
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Prefer getAttribute: an <input name="id"> shadows form.id on HTMLFormElement.
    const formId = form.getAttribute('id');
    if (!formId || form.getAttribute('is') !== 'product-form') return;

    const checkedOffers = getCheckedGiftOffersForForm(formId);
    if (!checkedOffers.length) return;

    const giftVariantIds = getGiftVariantIds(checkedOffers);
    if (!giftVariantIds.length) return;

    // Stop ProductForm._onSubmit — we handle both items ourselves.
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    submitProductFormWithGifts(form, giftVariantIds);
  },
  true
);

/**
 * Rebuy Smart Cart: show a "Free gift" badge when a Shopify discount code
 * whose name contains "free gift" is applied to this line item.
 * Used from the Rebuy cart-items Vue template via window.hasFreeGiftDiscount(item).
 */
function kaveeNormalizeDiscountLabel(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function kaveeIsFreeGiftLabel(value) {
  return kaveeNormalizeDiscountLabel(value).includes('freegift');
}

function kaveeGetRebuyCart() {
  return (
    window.Rebuy?.SmartCart?.cart ||
    window.Rebuy?.Cart?.cart ||
    (typeof window.Rebuy?.Cart?.getCart === 'function' ? window.Rebuy.Cart.getCart() : null) ||
    null
  );
}

window.hasFreeGiftDiscount = function hasFreeGiftDiscount(item) {
  const cart = kaveeGetRebuyCart();
  const codes = cart?.discount_codes || [];
  const rebuyCode = window.Rebuy?.SmartCart?.discount?.code;
  const cartHasFreeGiftCode =
    codes.some((discount) => discount && discount.applicable !== false && kaveeIsFreeGiftLabel(discount.code)) ||
    kaveeIsFreeGiftLabel(rebuyCode);

  if (!cartHasFreeGiftCode) return false;
  if (!item) return cartHasFreeGiftCode;

  const allocations = item.line_level_discount_allocations || [];
  if (allocations.length) {
    return allocations.some((allocation) => {
      const title =
        allocation?.discount_application?.title ||
        allocation?.discount_application?.code ||
        allocation?.title;
      return kaveeIsFreeGiftLabel(title);
    });
  }

  return Number(item.final_price) === 0 || Number(item.line_price) === 0;
};

/**
 * Rebuy Smart Cart: Forever Home Promise dropdown.
 * Theme settings and product.metafields.custom.trust_points are bridged via
 * window.kaveeCartTrust / window.kaveeTrustPoints because the Rebuy Vue
 * template is not processed as Liquid.
 */
const KAVEE_TRUST_LOCK_SVG =
  '<svg width="16" height="16" viewBox="0 0 15 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3.75 5C3.75 2.92893 5.42893 1.25 7.5 1.25C9.57107 1.25 11.25 2.92893 11.25 5V8.75H12.5V5C12.5 2.23858 10.2614 0 7.5 0C4.73858 0 2.5 2.23858 2.5 5V8.75H3.75V5Z" fill="#232323"/><rect x="0.625" y="8.125" width="13.75" height="11.25" rx="1.875" stroke="#232323" stroke-width="1.25" fill="none"/><path d="M7.56 16.7C7.93 16.3 8.23 15.95 8.43 15.7C8.54 15.56 8.63 15.45 8.68 15.38C8.79 15.23 8.88 15.09 9.02 14.91C9.17 14.68 9.39 14.37 9.61 14.01C10.06 13.28 10.49 12.47 10.6 11.94C10.7 11.48 10.56 11.26 10.44 11.15C10.31 11.04 10.11 10.97 9.9 10.99C9.73 11.01 9.5 11.09 9.32 11.22C9.09 11.35 8.84 11.52 8.61 11.69C8.38 11.86 8.17 12.03 8.02 12.15C7.94 12.21 7.88 12.26 7.84 12.3L7.42 12.66L7.03 12.4C6.98 12.37 6.9 12.32 6.9 12.33C6.82 12.28 6.7 12.2 6.56 12.12C6.27 11.95 5.88 11.75 5.48 11.58C5.07 11.41 4.7 11.29 4.43 11.28C4.29 11.27 4.23 11.28 4.2 11.29C3.99 11.71 4.02 12.05 4.15 12.38C4.21 12.56 4.31 12.73 4.42 12.92C4.51 13.08 4.66 13.31 4.75 13.49C4.89 13.73 5.41 14.44 6.05 15.18C6.37 15.55 6.69 15.91 6.99 16.2C7.21 16.42 7.41 16.59 7.56 16.7Z" stroke="#232323" stroke-width="1.25" fill="none"/></svg>';

const KAVEE_TRUST_CHECK_SVG =
  '<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5L4.36 9L11.91 1" stroke="#1A4B3D" stroke-width="2" stroke-linecap="round"/></svg>';

const KAVEE_TRUST_CHEVRON_SVG =
  '<svg width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m1 1.5 3 3 3-3" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';

function kaveeEscapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

function kaveeTrustProductId(item) {
  return String(item?.product_id || item?.product?.id || '');
}

function kaveeMergeTrustPoints(map) {
  if (!map || typeof map !== 'object') return;
  window.kaveeTrustPoints = window.kaveeTrustPoints || {};
  Object.keys(map).forEach((key) => {
    window.kaveeTrustPoints[String(key)] = map[key];
  });
}

function kaveeParseTrustPointsFromHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.getElementById('kavee-trust-points-data');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return {};
  }
}

function kaveeReadTrustPointsFromDom() {
  const el = document.getElementById('kavee-trust-points-data');
  if (!el) return;
  try {
    kaveeMergeTrustPoints(JSON.parse(el.textContent));
  } catch (e) {
    // Ignore invalid JSON
  }
}

window.kaveeGetTrustPoints = function kaveeGetTrustPoints(item) {
  const id = kaveeTrustProductId(item);
  const points = window.kaveeTrustPoints?.[id];
  if (!Array.isArray(points)) return [];
  return points.filter(Boolean).slice(0, 3);
};

window.kaveeHasTrustPoints = function kaveeHasTrustPoints(item) {
  return window.kaveeGetTrustPoints(item).length > 0;
};

function kaveeTrustPromiseIconHtml() {
  const icon = window.kaveeCartTrust?.icon;
  if (icon) {
    return `<img src="${kaveeEscapeHtml(icon)}" alt="" width="16" height="16">`;
  }
  return KAVEE_TRUST_LOCK_SVG;
}

function kaveeBuildTrustPromiseHtml(points) {
  const text = kaveeEscapeHtml(window.kaveeCartTrust?.text || '');
  const items = points
    .map(
      (point) =>
        `<li class="rebuy-cart__trust-promise-item"><span class="rebuy-cart__trust-promise-check" aria-hidden="true">${KAVEE_TRUST_CHECK_SVG}</span><span class="rebuy-cart__trust-promise-point">${kaveeEscapeHtml(point)}</span></li>`
    )
    .join('');

  return `<details class="rebuy-cart__trust-promise"><summary class="rebuy-cart__trust-promise-trigger"><span class="rebuy-cart__trust-promise-icon" aria-hidden="true">${kaveeTrustPromiseIconHtml()}</span><span class="rebuy-cart__trust-promise-text">${text}</span><span class="rebuy-cart__trust-promise-chevron" aria-hidden="true">${KAVEE_TRUST_CHEVRON_SVG}</span></summary><ul class="rebuy-cart__trust-promise-list" role="list">${items}</ul></details>`;
}

function kaveeHydrateTrustPromises() {
  if (!window.kaveeCartTrust?.enabled) return;

  const items = document.querySelectorAll(
    '[data-rebuy-component="cart-items"] li.rebuy-cart__flyout-item[data-product-id]'
  );

  items.forEach((itemEl) => {
    const productId = itemEl.getAttribute('data-product-id');
    const points = window.kaveeGetTrustPoints({ product_id: productId });
    const existing = itemEl.querySelector(':scope > .rebuy-cart__trust-promise');

    if (!points.length) {
      if (existing && existing.dataset.kaveeHydrated === 'true') existing.remove();
      return;
    }

    if (existing) return;

    itemEl.insertAdjacentHTML('beforeend', kaveeBuildTrustPromiseHtml(points));
    const inserted = itemEl.querySelector(':scope > .rebuy-cart__trust-promise');
    if (inserted) {
      inserted.dataset.kaveeHydrated = 'true';
      inserted.addEventListener('click', (event) => event.stopPropagation());
    }
  });
}

let kaveeTrustPointsRefreshTimer = null;
let kaveeTrustPointsRefreshing = false;
let kaveeCartItemsObserver = null;

function kaveeObserveCartItems() {
  const root = document.querySelector('[data-rebuy-component="cart-items"]');
  if (!root || kaveeCartItemsObserver) return;
  kaveeCartItemsObserver = new MutationObserver(() => kaveeHydrateTrustPromises());
  kaveeCartItemsObserver.observe(root, { childList: true, subtree: true });
  kaveeHydrateTrustPromises();
}

async function kaveeRefreshTrustPoints() {
  if (kaveeTrustPointsRefreshing) return;
  kaveeTrustPointsRefreshing = true;

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=cart-trust-points-data`);
    if (response.ok) {
      const data = await response.json();
      const html = data?.['cart-trust-points-data'];
      if (html) kaveeMergeTrustPoints(kaveeParseTrustPointsFromHtml(html));
    }
    kaveeObserveCartItems();
    kaveeHydrateTrustPromises();
  } catch (e) {
    kaveeObserveCartItems();
    kaveeHydrateTrustPromises();
  } finally {
    kaveeTrustPointsRefreshing = false;
  }
}

function kaveeScheduleTrustPointsRefresh() {
  clearTimeout(kaveeTrustPointsRefreshTimer);
  kaveeTrustPointsRefreshTimer = setTimeout(() => {
    kaveeRefreshTrustPoints();
  }, 150);
}

kaveeReadTrustPointsFromDom();
kaveeObserveCartItems();

['rebuy:cart.change', 'rebuy:cart.add', 'rebuy:smartcart.ready', 'rebuy:smartcart.show'].forEach((eventName) => {
  document.addEventListener(eventName, kaveeScheduleTrustPointsRefresh);
});
